import { useCallback, useMemo, useState } from 'react';
import { Box, ExpandableSection, SpaceBetween } from '@cloudscape-design/components';
import { toLower } from 'lodash';
import {
  AuditorUpdateTaskCommandInput,
  CreateTaskFromQuestionCommandInput,
  Task,
  TaskStatus,
  TaskType,
} from '@amzn/gbsa-helios-typescript-client';
import { TaskDetails } from 'src/types/helios/Question';
import { extractMediaIdSrcValues } from 'src/utils/mediaUtils';
import { Audit } from 'src/types/helios/Audit';
import TaskDraftForm from 'src/components/task/TaskDraftForm';
import { TaskDraftFormData } from 'src/validations/taskDraftFormSchema';
import { useCreateTaskDraftMutation } from 'src/data/api/helios-service-ts/task/queries/useCreateTaskDraftMutation';
import { useAuditorUpdateTaskMutation } from 'src/data/api/helios-service-ts/task/queries/useAuditorUpdateTaskMutation';
import useUserProfile from 'src/hooks/useUserProfile';
import { APP_PROGRAM_ID, DEFAULT_LANGUAGE } from 'src/contstant/commonLabels';
import { formatDateTime } from 'src/utils/generalUtils';

interface TaskDraftPanelProps {
  audit: Audit;
  questionId: string;
  questionVersion: string;
  subQuestionId?: string;
  questionTitle?: string;
  defaultTask?: TaskDetails;
  existingDraft?: Task;
  businessUnitId: string;
  divisionName: string;
  onError?: (message: string) => void;
  /** Font size for the expandable header title (default: "body-m") */
  headerFontSize?: 'body-m' | 'heading-s';
  /** Custom submit button label (default: "Save Task Draft") */
  submitButtonText?: string;
  /** Whether to hide the "(Optional)" label next to the submit button */
  hideOptionalLabel?: boolean;
  /** Whether to hide the "(Optional)" label in the expandable section header */
  hideHeaderOptionalLabel?: boolean;
  /** Color for the expandable section header text (default: "text-status-warning") */
  headerColor?: 'text-status-warning' | 'text-label';
  /** Whether to skip the ExpandableSection wrapper and render the form directly */
  hideExpandableWrapper?: boolean;
  /** Called after a successful create/update mutation */
  onMutationSuccess?: () => void;
  /** Task status to use when creating/updating (default: DRAFT) */
  taskStatusOverride?: TaskStatus;
  /** When true all form inputs and the submit button are disabled */
  disabled?: boolean;
  /** Custom header text override (default: "Create/Edit Task for Failed Question") */
  headerText?: string;
  /** Initial expanded state of the panel (default: true) */
  defaultExpanded?: boolean;
  /** When true, task is created with DATA_COLLECTION type instead of FAILED_QUESTION */
  isDataCollection?: boolean;
}

const TaskDraftPanel = ({
  audit,
  questionId,
  questionVersion,
  subQuestionId,
  questionTitle,
  defaultTask,
  existingDraft,
  businessUnitId,
  divisionName,
  onError,
  headerFontSize = 'body-m',
  submitButtonText = 'Save Task Draft',
  hideOptionalLabel = false,
  hideHeaderOptionalLabel = false,
  headerColor = 'text-status-warning',
  hideExpandableWrapper = false,
  onMutationSuccess,
  taskStatusOverride = TaskStatus.DRAFT,
  disabled = false,
  headerText,
  defaultExpanded = true,
  isDataCollection = false,
}: TaskDraftPanelProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showSuccess, setShowSuccess] = useState(false);
  const { getUserId } = useUserProfile();

  /** Map existingDraft/default (Task) fields into TaskDraftFormData for TaskDraftForm */
  const mergedInitialData: TaskDraftFormData | undefined = useMemo(() => {
    if (existingDraft) {
      // For a persisted user-saved draft we restore the value the operator
      // previously entered. When no duration was saved, the form stays blank
      // and the operator has to enter one before saving again.
      return {
        taskName: existingDraft.taskName || '',
        taskDescription: existingDraft.description || '',
        taskDurationDays: existingDraft.duration
          ? parseInt(existingDraft.duration as string)
          : undefined,
        assignee: existingDraft.assigneeLogin || '',
        auditorObservation: existingDraft.auditorObservation || '',
      };
    }
    if (defaultTask) {
      // We intentionally do NOT prefill from the question-defined default
      // duration — task creation must always be an explicit operator choice.
      // See Asana 1216940091847125 / RCA of overdue-task inflation.
      return {
        taskName: defaultTask.taskName || '',
        taskDescription: defaultTask.taskDescription || '',
        taskDurationDays: undefined,
        assignee: '',
        auditorObservation: '',
      };
    }
    return undefined;
  }, [existingDraft, defaultTask]);

  const mediaUploadParams = useMemo(
    () => ({
      programId: APP_PROGRAM_ID,
      businessUnit: audit.businessUnitId || audit.businessUnit,
      createdBy: getUserId(),
    }),
    [audit.businessUnitId, audit.businessUnit, getUserId],
  );

  const handleMutationError = useCallback(
    (error: Error) => {
      onError?.(`Failed to save task draft: ${error.message}`);
    },
    [onError],
  );

  const handleMutationSuccess = useCallback(() => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    onMutationSuccess?.();
  }, [onMutationSuccess]);

  const createDraftMutation = useCreateTaskDraftMutation(
    { businessUnitId },
    {
      onError: handleMutationError,
      onSuccess: handleMutationSuccess,
    },
  );

  const updateDraftMutation = useAuditorUpdateTaskMutation(
    {
      businessUnitId,
      taskId: existingDraft?.taskId ?? '',
    },
    {
      onError: handleMutationError,
      onSuccess: handleMutationSuccess,
    },
  );

  const handleSave = useCallback(
    (formData: TaskDraftFormData) => {
      // Post-validation invariant: taskDraftFormSchema guarantees
      // `taskDurationDays` is a number here. This guard narrows the widened
      // `number | undefined` union so the payload construction below can
      // safely call `String()` on it without producing "undefined" strings.
      // Matches the same guard in CreateTaskForm.handleSave.
      if (typeof formData.taskDurationDays !== 'number') return;
      const userId = getUserId();
      // Extract media IDs embedded in the processed description so the
      // backend can associate them with the task.
      const descriptionMediaIds = extractMediaIdSrcValues(formData.taskDescription);
      const taskMediaIdsMap =
        descriptionMediaIds.length > 0
          ? { taskDescription: descriptionMediaIds }
          : undefined;

      if (existingDraft) {
        updateDraftMutation.mutate({
          businessUnitId,
          taskId: existingDraft.taskId!,
          taskName: formData.taskName,
          description: formData.taskDescription,
          assigneeLogin: toLower(formData.assignee.trim()),
          auditorObservation: formData.auditorObservation || undefined,
          taskStatus: taskStatusOverride,
          duration: String(formData.taskDurationDays),
          lastUpdatedBy: userId,
          taskMediaIdsMap,
        } as AuditorUpdateTaskCommandInput);
      } else {
        const input = {
          businessUnitId,
          taskName: formData.taskName,
          description: formData.taskDescription,
          assigneeLogin: toLower(formData.assignee.trim()),
          taskType: isDataCollection
            ? TaskType.DATA_COLLECTION
            : TaskType.FAILED_QUESTION,
          taskStatus: taskStatusOverride,
          auditId: audit.auditId,
          locationId: audit.location,
          questionId,
          questionVersion,
          subQuestionId,
          divisionName,
          auditorObservation: formData.auditorObservation || undefined,
          auditName: audit.name,
          questionTitle,
          createdBy: userId,
          language: DEFAULT_LANGUAGE,
          templateId: audit.templateId,
          templateName: audit.templateName,
          duration: String(formData.taskDurationDays),
          taskMediaIdsMap,
        } as CreateTaskFromQuestionCommandInput;
        createDraftMutation.mutate(input);
      }
    },
    [
      existingDraft,
      updateDraftMutation,
      createDraftMutation,
      businessUnitId,
      audit,
      questionId,
      questionVersion,
      subQuestionId,
      divisionName,
      questionTitle,
      getUserId,
    ],
  );

  const formElement = (
    <TaskDraftForm
      onSubmit={handleSave}
      isSubmitting={createDraftMutation.isPending || updateDraftMutation.isPending}
      submitButtonText={submitButtonText}
      visible={hideExpandableWrapper || expanded}
      initialData={mergedInitialData}
      showSuccess={showSuccess}
      mediaUploadParams={mediaUploadParams}
      hideOptionalLabel={hideOptionalLabel}
      disabled={disabled}
    />
  );

  if (hideExpandableWrapper) {
    return <div data-testid="task-draft-panel">{formElement}</div>;
  }

  return (
    <div data-testid="task-draft-panel">
      <ExpandableSection
        data-testid="task-draft-expandable"
        variant="default"
        headerText={
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Box fontSize={headerFontSize} fontWeight="bold" color={headerColor}>
              {headerText
                ? headerText
                : existingDraft
                  ? 'Edit Task for Failed Question'
                  : 'Create Task for Failed Question'}
            </Box>
            {!hideHeaderOptionalLabel && (
              <Box fontSize="body-s" color="text-body-secondary">
                (Optional)
              </Box>
            )}
            {existingDraft?.lastUpdatedAt && (
              <Box color="text-body-secondary" fontSize="body-s">
                Last updated at {formatDateTime(existingDraft.lastUpdatedAt)}
              </Box>
            )}
          </SpaceBetween>
        }
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
      >
        {formElement}
      </ExpandableSection>
    </div>
  );
};

export default TaskDraftPanel;
